import { useEffect, useState } from 'react';
import { ApiError } from '../api';
import { linkProps } from '../router';
import { refreshTautulliStatus, tautulliApi } from '../tautulli';

/** Systeem → Tautulli koppelen (alleen de beheerder van de server). */
export function TautulliSettings() {
  const [cfg, setCfg] = useState<{ configured: boolean; url: string | null; inNav: boolean; widget: boolean; userStats: boolean } | null>(null);
  const [owner, setOwner] = useState(true);
  const [url, setUrl] = useState('');
  const [key, setKey] = useState('');
  const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null);
  const [busy, setBusy] = useState(false);

  const load = () => tautulliApi.config().then((c) => { setCfg(c); setUrl(c.url ?? ''); }).catch((e: unknown) => { if (e instanceof ApiError && e.status === 403) setOwner(false); });
  useEffect(() => {
    void load();
  }, []);
  if (!owner || !cfg) return null;

  const err = (e: unknown, fb: string) => (e instanceof ApiError && e.message !== e.code ? e.message : fb);

  const save = async () => {
    setBusy(true);
    setMsg(null);
    try {
      const r = await tautulliApi.save({ url, apiKey: key || undefined });
      setKey('');
      await load();
      const t = await tautulliApi.test().catch(() => null);
      setMsg({ ok: true, text: t ? `✓ Verbonden met ${t.server ?? 'Plex'} · Tautulli ${t.version ?? r.version ?? ''} · ${t.libraries} bibliotheken · ${t.users} gebruikers` : '✓ Opgeslagen' });
      refreshTautulliStatus();
    } catch (e) {
      setMsg({ ok: false, text: err(e, 'Koppelen mislukt.') });
    } finally {
      setBusy(false);
    }
  };

  const test = async () => {
    setBusy(true);
    try {
      const t = await tautulliApi.test();
      setMsg({ ok: true, text: `✓ Verbonden met ${t.server ?? 'Plex'} · Tautulli ${t.version ?? ''} · ${t.libraries} bibliotheken · ${t.users} gebruikers` });
    } catch (e) {
      setMsg({ ok: false, text: err(e, 'Test mislukt.') });
    } finally {
      setBusy(false);
    }
  };

  const toggle = async (k: 'inNav' | 'widget' | 'userStats', v: boolean) => {
    setCfg({ ...cfg, [k]: v });
    await tautulliApi.toggles({ [k]: v }).catch(() => undefined);
    refreshTautulliStatus();
  };

  return (
    <form className="settings-block form-grid" onSubmit={(e) => { e.preventDefault(); void save(); }}>
      <div className="account-head">
        <h2>Tautulli</h2>
        <span className={`status-pill ${cfg.configured ? 'ok' : 'warn'}`}>{cfg.configured ? 'Gekoppeld' : 'Niet gekoppeld'}</span>
        <span className="status-pill warn">Alleen beheerder</span>
      </div>
      <p className="subtle hint">
        Statistieken en wie er nu kijkt, uit je eigen Tautulli. De API-sleutel vind je in Tautulli onder Settings → Web Interface → API.
        Hij wordt versleuteld op de server bewaard; andere gebruikers zien dit niet.
      </p>
      <label>Tautulli-adres<input className="text-input" required value={url} onChange={(e) => setUrl(e.target.value)} placeholder="http://192.168.1.3:8181" inputMode="url" autoComplete="off" /></label>
      <label>API-sleutel<input className="text-input" type="password" required={!cfg.configured} value={key} onChange={(e) => setKey(e.target.value)} placeholder={cfg.configured ? 'Opgeslagen — alleen invullen om te wijzigen' : ''} autoComplete="off" /></label>
      {msg && <p className={msg.ok ? 'srv-ok' : 'error'} role="status">{msg.text}</p>}
      <div className="form-actions">
        <button className="btn btn-primary" type="submit" disabled={busy || !url || (!cfg.configured && !key)}>{busy ? 'Bezig…' : 'Opslaan'}</button>
        {cfg.configured && <button className="btn" type="button" disabled={busy} onClick={() => void test()}>Testen</button>}
        {cfg.configured && <button className="btn btn-ghost" type="button" onClick={async () => { await tautulliApi.remove().catch(() => undefined); refreshTautulliStatus(); void load(); setMsg(null); }}>Ontkoppelen</button>}
      </div>
      {cfg.configured && (
        <>
          <label className="studio-toggle"><span>Server in het hoofdmenu <span className="subtle">— een eigen tab, alleen voor jou</span></span><span className="toggle"><input type="checkbox" checked={cfg.inNav} onChange={(e) => void toggle('inNav', e.target.checked)} /></span></label>
          <label className="studio-toggle"><span>"Nu actief" als widget <span className="subtle">— kies hem in de startscherm-editor</span></span><span className="toggle"><input type="checkbox" checked={cfg.widget} onChange={(e) => void toggle('widget', e.target.checked)} /></span></label>
          <label className="studio-toggle"><span>Kijkstatistieken bij Gebruikers &amp; rechten</span><span className="toggle"><input type="checkbox" checked={cfg.userStats} onChange={(e) => void toggle('userStats', e.target.checked)} /></span></label>
          <div className="form-actions"><a className="btn" {...linkProps('/server')}>Open de Server-pagina</a></div>
        </>
      )}
    </form>
  );
}
